/**
 * Adapter Layer - Makes API requests for user data
 * This layer is responsible for the raw HTTP communication
 */

import { apiClient } from '../config/api-client'
import type {
  AdminUser,
  CurrentUser,
  Department,
  HubSpotSyncResponse,
  HubspotOwner,
  UpdateUserPayload,
} from '@/types/user.types'

/** Fetch the signed-in user's identity, department and effective permissions. */
export async function getCurrentUserApi(): Promise<CurrentUser> {
  return apiClient.get<CurrentUser>('/me')
}

export async function getAllUsersApi(): Promise<Array<AdminUser>> {
  return apiClient.get<Array<AdminUser>>(`/users`)
}

export async function updateUserApi(
  userId: string,
  payload: UpdateUserPayload,
): Promise<AdminUser> {
  return apiClient.patch<AdminUser>(`/users/${userId}`, payload)
}

export async function updateUserDepartmentApi(
  userId: string,
  department: Department,
): Promise<AdminUser> {
  return apiClient.patch<AdminUser>(`/users/${userId}/department`, {
    department,
  })
}

export async function activateUserApi(userId: string): Promise<void> {
  return apiClient.post<void>(`/users/${userId}/activate`)
}

export async function deactivateUserApi(
  userId: string,
  deleted?: boolean,
): Promise<void> {
  const query = deleted ? '?deleted=true' : ''
  return apiClient.post<void>(`/users/${userId}/deactivate${query}`)
}

export async function syncUserHubSpotApi(
  email: string,
): Promise<HubSpotSyncResponse> {
  return apiClient.post<HubSpotSyncResponse>('/users/sync-hubspot', { email })
}

export async function deleteUserApi(userId: string): Promise<void> {
  return apiClient.delete<void>(`/users/${userId}`)
}

export async function getUserByIdApi(userId: string): Promise<AdminUser> {
  return apiClient.get<AdminUser>(`/users/${userId}`)
}

export async function uploadProfilePictureApi(
  userId: string,
  file: File,
): Promise<{ bucketKey: string; url: string }> {
  const formData = new FormData()
  formData.append('file', file)
  return apiClient.instance
    .post(`/users/${userId}/profile-picture`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((r) => r as unknown as { bucketKey: string; url: string })
}

export async function deleteProfilePictureApi(userId: string): Promise<void> {
  return apiClient.delete<void>(`/users/${userId}/profile-picture`)
}

/**
 * Fetch active users who have a hubspotOwnerId — used to populate owner dropdowns
 */
export async function getHubspotOwnersApi(): Promise<Array<HubspotOwner>> {
  return apiClient.get<Array<HubspotOwner>>('/users/hubspot-owners')
}

/**
 * Record that an admin has vouched for a user's access level without changing
 * it — the department endpoint only stamps on a CHANGE.
 */
export async function confirmUserDepartmentApi(userId: string) {
  return apiClient.post(`/users/${userId}/confirm-department`, {})
}
